import React, { useState, useEffect, useMemo } from 'react';
import useSWR from 'swr';
import { LovableModal } from '@nearst/ui';
import { Table, TableBody, TableCell, TableHead, TableRow, CircularProgress } from '@material-ui/core';
import * as Sentry from '@sentry/browser';
import { setQuantityBuffer } from '@services/shop';
import { useShop } from '@services/ShopProvider';
import { defaultFilters } from '@services/stock';
import { listInventory } from '@services/stockSearch';

import styles from './QuantityBufferModal.module.scss';

const QuantityBufferModal = ({ open, close, shop }) => {
	const { mutate, organisation, data: shops } = useShop();
	const isOrganisationLevel = !shop?.id;

	// Determine initial buffer value
	const getInitialBuffer = () => {
		if (!isOrganisationLevel) {
			return shop?.quantityBuffer || 0;
		}
		// For org view, check if all shops have the same buffer
		const bufferValues = shops?.map(s => s.quantityBuffer || 0) || [];
		const allSame = bufferValues.length > 0 && bufferValues.every(v => v === bufferValues[0]);
		return allSame ? bufferValues[0] : 0;
	};

	const [buffer, setBuffer] = useState(getInitialBuffer());
	const [loading, setLoading] = useState(false);
	const [errorMsg, setErrorMsg] = useState('');
	const [successMsg, setSuccessMsg] = useState('');

	// Fetch a small sample of inventory for the preview (not dependent on URL state)
	// Only fetch when modal is open to avoid unnecessary API calls
	// Uses listInventory which works for both shop-level and org-level data
	const sampleKey = open ? `sample-inventory-${shop?.id || 'org'}-${organisation.id}` : null;
	const { data: sampleInventory } = useSWR(
		sampleKey,
		() => listInventory({
			organisationId: organisation.id,
			shopId: shop?.id || '',
			page: 1,
			query: '',
			filters: defaultFilters
		}),
		{ revalidateOnFocus: false, dedupingInterval: 60000 }
	);

	// Get sample products for preview (max 5, in-stock only)
	const sampleProducts = useMemo(() => {
		if (!sampleInventory?.inventory) return [];
		const inStock = sampleInventory.inventory.filter(item => item.in_stock);
		// Use in-stock products if available, otherwise fall back to all products
		const productsToShow = inStock.length > 0 ? inStock : sampleInventory.inventory;
		return productsToShow.slice(0, 5);
	}, [sampleInventory]);

	// Check buffer consistency for org view
	const bufferConsistencyMessage = useMemo(() => {
		if (!isOrganisationLevel || !shops) return null;

		const bufferValues = shops.map(s => s.quantityBuffer || 0);
		const allSame = bufferValues.every(v => v === bufferValues[0]);

		if (allSame && bufferValues[0] > 0) {
			return `All ${shops.length} shop${shops.length > 1 ? 's' : ''} in your organisation currently have a buffer of ${bufferValues[0]} unit${bufferValues[0] > 1 ? 's' : ''}. Setting a new buffer will update all shops.`;
		} else if (allSame && bufferValues[0] === 0) {
			return `All ${shops.length} shop${shops.length > 1 ? 's' : ''} in your organisation have no buffer set. Setting a buffer will apply to all shops.`;
		} else {
			return `Your organisation has ${shops.length} shops with different buffer values. Setting a buffer will update all shops.`;
		}
	}, [isOrganisationLevel, shops]);

	// Update buffer when shop data changes
	useEffect(() => {
		setBuffer(getInitialBuffer());
	}, [shop, shops]);

	const handleIncrement = () => {
		setBuffer(prev => Math.min(prev + 1, 999));
		setErrorMsg('');
		setSuccessMsg('');
	};

	const handleDecrement = () => {
		if (buffer > 0) {
			setBuffer(prev => prev - 1);
			setErrorMsg('');
			setSuccessMsg('');
		}
	};

	const handleInputChange = (e) => {
		const value = parseInt(e.target.value) || 0;
		setBuffer(Math.max(0, Math.min(999, value)));
		setErrorMsg('');
		setSuccessMsg('');
	};

	const handleSetBuffer = async () => {
		if (buffer < 0) {
			setErrorMsg('Buffer cannot be negative.');
			return;
		}

		setLoading(true);
		setErrorMsg('');
		try {
			// Pass shop.id for shop-level, null/undefined for organisation-level
			// If buffer is 0, pass null to remove the buffer
			const bufferValue = buffer === 0 ? null : buffer;
			const result = await setQuantityBuffer(shop?.id, bufferValue);
			await mutate();

			// Customise success message based on context
			const successMessage = isOrganisationLevel
				? `Stock confidence buffer updated for ${result.shopsUpdated || 'all'} shop${result.shopsUpdated !== 1 ? 's' : ''} in ${organisation?.name || 'the organisation'}. It may take up to 48 hours for all quantities to be updated.`
				: 'Stock confidence buffer updated. It may take up to 48 hours for all quantities to be updated.';

			setSuccessMsg(successMessage);
		} catch (e) {
			console.error(e);
			Sentry.captureException(e);
			setErrorMsg('Something went wrong. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		setErrorMsg('');
		setSuccessMsg('');
		setBuffer(getInitialBuffer());
		close();
	};

	// Calculate how many products will be hidden
	const hiddenProductCount = useMemo(() => {
		return sampleProducts.filter(item => {
			const quantity = isOrganisationLevel ? (item.inStockTotal || 0) : (item.quantity_i || 0);
			return quantity - buffer <= 0;
		}).length;
	}, [sampleProducts, buffer, isOrganisationLevel]);

	const getSummaryMessage = () => {
		if (buffer === 0) {
			return 'No buffer set — all stock will be visible to customers.';
		}

		return (
			<>
				Customers will see <strong>{buffer} fewer units</strong> per product than you have in stock.
				{hiddenProductCount > 0 && (
					<>
						{' '}
						<strong>
							{hiddenProductCount} product{hiddenProductCount > 1 ? 's' : ''} in preview above will be hidden
						</strong>{' '}
						from your store.
					</>
				)}
			</>
		);
	};

	return (
		<LovableModal
			isOpen={open}
			onDismiss={handleClose}
			title="Set your stock confidence buffer"
			showDismissButton={false}
		>
			<div className={styles.quantityBufferModalWrapper}>
			<div className={styles.container}>
				<p className={styles.subtitle}>
					{isOrganisationLevel
						? `Choose how many units to hold back across all shops in ${organisation?.name || 'your organisation'}.`
						: 'Choose how many units to hold back.'}
				</p>

				{isOrganisationLevel && bufferConsistencyMessage && (
					<p className={styles.consistencyMessage}>
						{bufferConsistencyMessage}
					</p>
				)}

				<div className={styles.bufferControl}>
					<label className={styles.bufferLabel}>Buffer amount</label>
					<div className={styles.stepperContainer}>
						<div className={styles.stepper}>
							<button
								className={styles.stepperButton}
								onClick={handleDecrement}
								disabled={buffer === 0 || loading}
								aria-label="Decrease buffer"
							>
								−
							</button>
							<div className={styles.stepperDivider} />
							<input
								type="number"
								className={styles.stepperInput}
								value={buffer}
								onChange={handleInputChange}
								min="0"
								max="999"
								disabled={loading}
							/>
							<div className={styles.stepperDivider} />
							<button
								className={styles.stepperButton}
								onClick={handleIncrement}
								disabled={loading}
								aria-label="Increase buffer"
							>
								+
							</button>
						</div>
						<span className={styles.helperText}>
							units held back from customers so you never oversell
						</span>
					</div>
				</div>

				{sampleProducts.length > 0 && (
					<div className={styles.previewSection}>
						<h3 className={styles.previewTitle}>How your stock will appear</h3>
						{isOrganisationLevel && (
							<p className={styles.shopIndicator}>
								Example aggregate data across all shops in your organisation
							</p>
						)}
						<div className={styles.previewTable}>
							<Table>
								<TableHead>
									<TableRow>
										<TableCell>PRODUCT</TableCell>
										<TableCell align="right">IN STOCK</TableCell>
										<TableCell align="right">SHOWN TO CUSTOMERS</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
								{sampleProducts.map((item) => {
									const quantity = isOrganisationLevel ? (item.inStockTotal || 0) : (item.quantity_i || 0);
									const shownToCustomers = Math.max(0, quantity - buffer);
									return (
										<TableRow key={item.barcode}>
											<TableCell>{item.title || 'Untitled product'}</TableCell>
											<TableCell align="right">{quantity}</TableCell>
											<TableCell align="right">
												{shownToCustomers === 0 ? (
													<span className={styles.notShownBadge}>
														NOT SHOWN
													</span>
												) : (
													<span className={shownToCustomers <= 3 ? styles.stockWarning : styles.stockNormal}>
														{shownToCustomers}
													</span>
												)}
											</TableCell>
										</TableRow>
									);
								})}
								</TableBody>
							</Table>
						</div>
					</div>
				)}

				<div className={styles.summaryBox}>
					{getSummaryMessage()}
				</div>

				{errorMsg && <p className={styles.error}>{errorMsg}</p>}
				{successMsg && <p className={styles.success}>{successMsg}</p>}

				<div className={styles.footer}>
					<button
						disabled={loading}
						onClick={handleSetBuffer}
						className={styles.setBufferButton}
					>
						{loading ? (
							<>
								<CircularProgress size={16} thickness={4} style={{ color: 'white', marginRight: '8px' }} />
								Updating...
							</>
						) : (
							buffer === 0 ? 'Set buffer' : `Set buffer to ${buffer}`
						)}
					</button>
				</div>
			</div>
			</div>
		</LovableModal>
	);
};

export { QuantityBufferModal };
